import { supabase } from '@/integrations/supabase/client';

export interface UserDocument {
  id: string;
  user_id: string;
  chat_id: string | null;
  message_id: string | null;
  title: string;
  document_type: string;
  content: string;
  is_favorite: boolean;
  created_at: string;
}

export type DocumentType = 
  | 'acta'
  | 'informe'
  | 'decreto'
  | 'dictamen'
  | 'resolucion'
  | 'contrato'
  | 'recurso'
  | 'escrito'
  | 'otro';

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  acta: 'Acta',
  informe: 'Informe',
  decreto: 'Decreto',
  dictamen: 'Dictamen',
  resolucion: 'Resolución',
  contrato: 'Contrato',
  recurso: 'Recurso',
  escrito: 'Escrito',
  otro: 'Otro',
};

export async function saveDocument(
  userId: string,
  data: {
    title: string;
    content: string;
    document_type: string;
    chat_id?: string;
    message_id?: string;
  }
): Promise<UserDocument> {
  const { data: doc, error } = await supabase
    .from('user_documents')
    .insert({
      user_id: userId,
      title: data.title,
      content: data.content,
      document_type: data.document_type,
      chat_id: data.chat_id || null,
      message_id: data.message_id || null,
    })
    .select()
    .single();

  if (error) throw error;
  return doc as UserDocument;
}

export async function listDocuments(
  userId: string,
  filters?: {
    document_type?: string;
    favorites_only?: boolean;
    search?: string;
  }
): Promise<UserDocument[]> {
  let query = supabase
    .from('user_documents')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (filters?.document_type && filters.document_type !== 'all') {
    query = query.eq('document_type', filters.document_type);
  }

  if (filters?.favorites_only) {
    query = query.eq('is_favorite', true);
  }

  if (filters?.search) {
    query = query.ilike('title', `%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []) as UserDocument[];
}

export async function toggleDocumentFavorite(
  documentId: string,
  isFavorite: boolean
): Promise<void> {
  const { error } = await supabase
    .from('user_documents')
    .update({ is_favorite: isFavorite })
    .eq('id', documentId);

  if (error) throw error;
}

export async function deleteDocument(documentId: string): Promise<void> {
  const { error } = await supabase
    .from('user_documents')
    .delete()
    .eq('id', documentId);

  if (error) throw error;
}

export async function getDocument(documentId: string): Promise<UserDocument | null> {
  const { data, error } = await supabase
    .from('user_documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as UserDocument;
}

// Helper to detect document type from content
export function detectDocumentType(content: string): DocumentType {
  const lowerContent = content.toLowerCase();
  
  if (lowerContent.includes('acta de') || lowerContent.includes('acta del pleno')) {
    return 'acta';
  }
  if (lowerContent.includes('informe jurídico') || lowerContent.includes('informe técnico')) {
    return 'informe';
  }
  if (lowerContent.includes('decreto') || lowerContent.includes('decretar')) {
    return 'decreto';
  }
  if (lowerContent.includes('dictamen') || lowerContent.includes('dictamina')) {
    return 'dictamen';
  }
  if (lowerContent.includes('resolución') || lowerContent.includes('resuelvo')) {
    return 'resolucion';
  }
  if (lowerContent.includes('contrato') || lowerContent.includes('contratación')) {
    return 'contrato';
  }
  if (lowerContent.includes('recurso') || lowerContent.includes('interponer')) {
    return 'recurso';
  }
  if (lowerContent.includes('escrito') || lowerContent.includes('solicitud')) {
    return 'escrito';
  }
  
  return 'otro';
}
